import { unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";
import { partnerName, productInfo, productInfos, productSupplier, warehouses } from "../lookups.ts";
import type {
  Advice,
  Basket,
  Expected,
  Frequent,
  LineChangeBody,
  NewLineBody,
  NewPaymentBody,
  Order,
  OrderBasketBody,
  OrderFilter,
  OrderState,
  Payment,
  PaymentStatus,
  PriceSource,
  ProductInfo,
  Supplier,
} from "./types.ts";

/*
 * Закупки на REST из contracts/openapi.json (модуль purchasing, PR #38).
 * В ответах только id товара, поставщика и склада: названия добираются
 * из справочника (lookups.ts). Оплаты списком и кто подтвердил контракт не
 * отдаёт; если сервер начнёт их присылать, экран их покажет.
 */
type S = components["schemas"];

const id = (v: string) => ({ params: { path: { id: v } } });

async function names(productIds: string[]): Promise<(pid: string) => ProductInfo> {
  const infos = await productInfos(productIds);
  return (pid) => {
    const p = infos.get(pid);
    return { productSku: p?.sku ?? pid, productName: p?.name ?? pid, uom: p?.uom ?? "шт" };
  };
}

async function destinationName(): Promise<(wid: string) => string> {
  const list = await warehouses();
  return (wid) => list.find((w) => w.id === wid)?.name ?? wid;
}

/** Поля сверх контракта, если сервер их уже присылает. */
type Extra = { payments?: Payment[]; confirmedBy?: string | null; needDate?: string | null };

async function order(o: S["PurchasingOrder"]): Promise<Order> {
  const [info, dest, supplierName] = await Promise.all([names(o.lines.map((l) => l.productId)), destinationName(), partnerName(o.supplierId)]);
  const extra = o as S["PurchasingOrder"] & Extra;
  return {
    ...o,
    state: o.state as OrderState,
    paymentStatus: o.paymentStatus as PaymentStatus,
    family: o.family ?? null,
    expectedArrival: o.expectedArrival ?? null,
    invoice: o.invoice ?? null,
    supplierName,
    confirmedBy: extra.confirmedBy ?? null,
    payments: extra.payments ?? [],
    lines: o.lines.map((l) => ({
      ...l,
      ...info(l.productId),
      family: l.family ?? null,
      priceSource: l.priceSource as PriceSource,
      destinationName: dest(l.destination),
    })),
  };
}

const orderOf = async (p: Promise<{ data?: S["PurchasingOrder"]; error?: unknown; response: Response }>) => order(await unwrap(p));

export const purchasingApi = {
  baskets: async (): Promise<Basket[]> => {
    const list = await unwrap(api.GET("/api/v1/purchasing/baskets"));
    const [info, dest] = await Promise.all([names(list.flatMap((b) => b.lines.map((l) => l.productId))), destinationName()]);
    return list.map((b) => ({
      supplierId: b.supplierId ?? null,
      supplierName: b.supplierName ?? null,
      lines: b.lines.map((l) => ({
        ...l,
        ...info(l.productId),
        amount: l.amount ?? null,
        destinationName: dest(l.destination),
        needDate: (l as typeof l & Extra).needDate ?? null,
      })),
    }));
  },
  orderBasket: (body: OrderBasketBody) => orderOf(api.POST("/api/v1/purchasing/baskets/order", { body })),
  assignSupplier: (productId: string, supplierId: string) =>
    unwrap(api.POST("/api/v1/purchasing/baskets/assign", { params: { query: { productId, supplierId } } })).then(() => null),
  orders: async (filters: OrderFilter[]): Promise<Order[]> =>
    Promise.all((await unwrap(api.GET("/api/v1/purchasing/orders", { params: { query: { filter: filters } } }))).map(order)),
  order: (v: string) => orderOf(api.GET("/api/v1/purchasing/orders/{id}", id(v))),
  createOrder: (supplierId: string, family: string | null) => orderOf(api.POST("/api/v1/purchasing/orders", { body: { supplierId, family } })),
  changeOrder: (v: string, family: string | null) => orderOf(api.PATCH("/api/v1/purchasing/orders/{id}", { ...id(v), body: { family } })),
  addLine: (v: string, body: NewLineBody) => orderOf(api.POST("/api/v1/purchasing/orders/{id}/lines", { ...id(v), body })),
  changeLine: (v: string, no: number, body: LineChangeBody) =>
    orderOf(api.PATCH("/api/v1/purchasing/orders/{id}/lines/{no}", { params: { path: { id: v, no } }, body })),
  confirm: (v: string, expectedArrival: string | null) => orderOf(api.POST("/api/v1/purchasing/orders/{id}/confirm", { ...id(v), body: { expectedArrival } })),
  approve: (v: string) => orderOf(api.POST("/api/v1/purchasing/orders/{id}/approve", id(v))),
  cancel: (v: string) => orderOf(api.POST("/api/v1/purchasing/orders/{id}/cancel", id(v))),
  invoice: (v: string, invoice: string) => orderOf(api.POST("/api/v1/purchasing/orders/{id}/invoice", { ...id(v), body: { invoice } })),
  pricesConfirmed: (v: string) => orderOf(api.POST("/api/v1/purchasing/orders/{id}/prices-confirmed", id(v))),
  reprice: (v: string, no: number, unitPrice: number) =>
    orderOf(api.POST("/api/v1/purchasing/orders/{id}/lines/{no}/reprice", { params: { path: { id: v, no } }, body: { unitPrice } })),
  payment: (v: string, body: NewPaymentBody) => orderOf(api.POST("/api/v1/purchasing/orders/{id}/payments", { ...id(v), body })),

  /** Ожидаемые поставки: поставщик, дата и сумма из заказов «не отримано». */
  expected: async (): Promise<Expected[]> => {
    const [list, orders] = await Promise.all([
      unwrap(api.GET("/api/v1/purchasing/expected")),
      unwrap(api.GET("/api/v1/purchasing/orders", { params: { query: { filter: ["NOT_RECEIVED"] } } })),
    ]);
    return Promise.all(
      list.map(async (e) => {
        const o = orders.find((x) => x.id === e.orderId);
        return {
          ...e,
          paymentStatus: e.paymentStatus as PaymentStatus,
          supplierName: o ? await partnerName(o.supplierId) : "",
          expectedArrival: o?.expectedArrival ?? o?.createdAt ?? "",
          gross: o?.gross ?? 0,
          currency: o?.currency ?? "UAH",
        };
      }),
    );
  },

  /** «Часто замовляєте»: поставщик товара из его карточки в справочнике. */
  frequent: async (): Promise<Frequent[]> => {
    const list = await unwrap(api.GET("/api/v1/purchasing/frequent"));
    const info = await names(list.map((f) => f.productId));
    return Promise.all(
      list.map(async (f) => {
        const supplier = await productSupplier(f.productId);
        return { ...f, ...info(f.productId), supplierId: supplier?.id ?? null, supplierName: supplier?.name ?? null };
      }),
    );
  },

  /** Поставщики это контрагенты справочника с отметкой «постачальник». */
  suppliers: async (): Promise<Supplier[]> => {
    const list = await unwrap(api.GET("/api/v1/partners", { params: { query: { q: "", limit: 200 } } }));
    return list
      .filter((p) => p.supplier && !p.archived)
      .map((p) => ({ id: p.id, name: p.name, edrpou: p.registryCode ?? null, vatPayer: p.vatPayer, currency: p.currency, kind: "COMPANY" as const }));
  },

  advice: (warehouseId: string, productId: string): Promise<Advice> =>
    unwrap(api.GET("/api/v1/purchasing/advice", { params: { query: { warehouseId, productId } } })).then((a) => ({
      ...a,
      dailyUse: a.dailyUse ?? null,
      daysOfStock: a.daysOfStock ?? null,
    })),
  reconciliation: () => unwrap(api.GET("/api/v1/purchasing/tasks/reconciliation")),
};

/**
 * «Часто замовляєте» одним нажатием: строка в открытый черновик
 * поставщика, а если его нет, в новый. Тремя вызовами контракта.
 */
export async function addToDraft(f: { supplierId: string; productId: string; qty: number }): Promise<Order> {
  const drafts = await purchasingApi.orders(["DRAFTS"]);
  const draft = drafts.find((o) => o.supplierId === f.supplierId && o.state === "DRAFT") ?? (await purchasingApi.createOrder(f.supplierId, null));
  return purchasingApi.addLine(draft.id, { productId: f.productId, qty: f.qty, destination: "MAIN", requestLineIds: [] });
}

/** Товар с названием для строк, где контракт дал только id. */
export const productName = (pid: string) => productInfo(pid).then((p) => p.name);
